import {
    useCallback,
    useEffect,
    useState,
    useMemo,
} from 'react'
import moment from 'moment'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'

import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useGetFreightForwarder from 'app/Apps/OrderManagement/hooks/useGetFreightForwarder'
import {
    OrderStep,
    StepConfiguration,
} from 'app/Apps/OrderManagement/Orders/orders.types'
import {
    LocationType, StepTypeDetailed,
    OrderTransportModeRoad, DeliveryOptionRoad, SpecificDelayReason,
} from 'app/enums/label.enums'
import useEnumValues from 'app/hooks/useEnumValues'
import validateForm from 'app/utils/validateForm'
import {
    strToDate,
} from 'app/utils/date'

import useGetFieldsWithConfiguration from '../../hooks/useGetFieldsWithConfiguration'
import isTypeTransport from '../../utils/isTypeTransport'
import getStepRoadFormFields from './fields/stepRoadForm.fields'
import getStepAirFormFields from './fields/stepAirForm.fields'
import getStepEventFormFields from './fields/stepEventForm.fields'
import getStepSeaFormFields from './fields/stepSeaForm.fields'
import getStepCustomsFormFields from './fields/stepCustomsForm.fields'
import getStepConfirmationFormFields from './fields/stepConfirmationForm.fields'
import useStyles from './OrderStepsForm.style'
import specificDelayReasonMapping from './specificDelayReasonMapping'

type OnChangeEvent = {
    target: {
        name: number,
        value: Partial<OrderStep>,
    },
}

type Props = {
    value: Partial<OrderStep>,
    onChange: (changeValue: Partial<OrderStep>, event: OnChangeEvent) => void,
    stepConfiguration: StepConfiguration,
    location?: { id: number },
    pickupLocationContacts?: { id: number }[],
    baseLeaseUntilTimestamp?: string,
    disabled: boolean,
    isModified: boolean,
    exists: boolean,
    isFirstStep: boolean,
    setIsFormValid?: (isFormValid: boolean) => void,
}

const defaultProps: Partial<Props> = {
    location: undefined,
    baseLeaseUntilTimestamp: undefined,
    pickupLocationContacts: undefined,
    setIsFormValid: undefined,
}

const PICKUP_LOCATION = 'pickupLocation'
const LOCATION = 'location'
const DOMAIN_NAME = 'OrderStep'
const TRANSPORT_MODE_LCL = 'LCL'
const FIELDS = {
    ROAD: getStepRoadFormFields,
    SEA: getStepSeaFormFields,
    AIR: getStepAirFormFields,
    CUSTOMS: getStepCustomsFormFields,
    EVENT: getStepEventFormFields,
    CONFIRMATION: getStepConfirmationFormFields,
}
const FILTER_LOCATION_TYPE = {
    SEA: LocationType.SEAPORT,
    AIR: LocationType.AIRPORT,
}

const MAP_DELIVERY_TIME = {
    LTL: {
        earlyTime: -240,
        delayTime: 240,
    },
    FTL: {
        earlyTime: -60,
        delayTime: 60,
    },
}

const checkIsDelay = ({
    actual,
    requested,
    earlyTime,
    delayTime,
}: {
    actual: string,
    requested: string,
    earlyTime: number,
    delayTime: number,
}): boolean => {
    const diffHours = moment(strToDate(actual), 'X')
        .diff(moment(strToDate(requested), 'X'), 'minutes')

    return diffHours <= earlyTime || diffHours >= delayTime
}

const OrderStepsForm = (props: Props) => {
    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()
    const {
        stepConfiguration,
        value,
        location: defaultLocation,
        baseLeaseUntilTimestamp,
        pickupLocationContacts,
        isModified,
        onChange,
        disabled,
        exists,
        isFirstStep,
        setIsFormValid,
    } = props

    const {
        stepType,
        transportModeRoad,
        transportModeForwarder,
        forwarder,
        pickupLocation,
        deliveryLocation,
        informationEntered = moment().format('DD.MM.YYYY HH:mm ZZ'),
        location,
        transportModeSea,
        stepTypeDetailed,
        seaFreightContainerSize,
        deliveryOptionRoad,
        forwarderContacts,
        mainDelayReason,
        specificDelayReason,
        delayReasonComment,
        actualDelivery,
        requestedDelivery,
    } = value

    const {
        data: freightForwarderData,
    } = useGetFreightForwarder(value?.forwarder?.id)

    const [
        defaultPickupLocationContacts,
        setDefaultPickupLocationContacts,
    ] = useState<{ id: number }[]>()

    const onChangeValue = useCallback((newValue: Partial<OrderStep>): void => {
        onChange(newValue, {
            target: {
                name: value.id,
                value: newValue,
            },
        })
    }, [
        onChange,
        value.id,
    ])

    const [
        defaultTransportModeRoad,
        setTransportModeRoad,
    ] = useState<OrderTransportModeRoad>(transportModeRoad)

    const [
        defaultTransportModeForwarder,
        setTransportModeForwarder,
    ] = useState<OrderTransportModeRoad>(transportModeForwarder)

    const [
        defaultDeliveryOptionRoad,
        setDefaultDeliveryOptionRoad,
    ] = useState<DeliveryOptionRoad>(deliveryOptionRoad)

    const [
        defaultForwarderId,
        setDefaultForwarderId,
    ] = useState<number>(forwarder?.id)

    const isRoadDelivery = value.stepTypeDetailed === StepTypeDetailed.ROAD_DELIVERY

    const isTransportStep: boolean = useMemo(() => {
        return isTypeTransport(stepType)
    }, [stepType])

    const locationProperty: string = useMemo(() => {
        return isTransportStep ? PICKUP_LOCATION : LOCATION
    }, [isTransportStep])

    const isShowMainDelayReason: boolean = useMemo(() => {
        if (!actualDelivery || !requestedDelivery || !transportModeRoad) {
            return false
        }

        return checkIsDelay({
            actual: actualDelivery,
            requested: requestedDelivery,
            earlyTime: MAP_DELIVERY_TIME[transportModeRoad].earlyTime,
            delayTime: MAP_DELIVERY_TIME[transportModeRoad].delayTime,
        })
    }, [
        actualDelivery,
        requestedDelivery,
        transportModeRoad,
    ])

    const isShowSpecificDelayReason = mainDelayReason && ![
        'EARLY_DELIVERY_CUSTOMER_ACCEPTED',
        'DELAY_CAUSED_BY_MULTIPLE_TRUCKS_SLOTS',
        'DELAY_CAUSED_BY_CUSTOMER',
    ].includes(mainDelayReason)

    const fieldsWithClassName = useFieldsWithClassName(FIELDS[stepType]({
        locationId: location?.id,
        pickupLocationId: pickupLocation?.id,
        deliveryLocationId: deliveryLocation?.id,
        informationEntered,
        baseLeaseUntilTimestamp,
        filterLocationType: FILTER_LOCATION_TYPE[stepType],
        exists,
        isFirstStep,
        transportModeSea,
        transportModeForwarder,
        transportModeRoad,
        getAllowedValues,
        isRoadDelivery,
        stepTypeDetailed,
        seaFreightContainerSize,
        deliveryOptionRoad,
        isShowMainDelayReason,
        isShowSpecificDelayReason,
        isShowDelayReasonComment: specificDelayReason === SpecificDelayReason.OTHER,
        specificDelayReasonValues: specificDelayReasonMapping[mainDelayReason],
    }), classes)

    const fieldsWithConfiguration = useGetFieldsWithConfiguration(
        stepConfiguration,
        fieldsWithClassName,
        disabled,
    )

    const modifiedFields = useMemo(() => {
        return fieldsWithConfiguration.map((field) => {
            return {
                ...field,
                fields: field.fields.map((item) => {
                    if (item.name === 'baseLeaseUntilTimestamp') {
                        return item
                    }
                    return {
                        ...item,
                        disabled: false,
                    }
                }),
            }
        })
    }, [fieldsWithConfiguration])

    const fields = useMemo(() => {
        return isModified ? modifiedFields : fieldsWithConfiguration
    }, [
        fieldsWithConfiguration,
        isModified,
        modifiedFields,
    ])

    useEffect(() => {
        if (setIsFormValid) {
            setIsFormValid(
                validateForm({
                    fields,
                    value,
                }),
            )
        }
    }, [
        setIsFormValid,
        fields,
        value,
    ])

    useEffect(() => {
        let updatedValue = {}

        if (!isRoadDelivery && (defaultTransportModeRoad !== transportModeRoad)) {
            setTransportModeRoad(transportModeRoad)
            updatedValue = {
                ...updatedValue,
                deliveryOptionRoad: undefined,
                numberOfTrucks: undefined,
                numberOfVans: undefined,
            }
        }

        if (defaultDeliveryOptionRoad !== deliveryOptionRoad) {
            setDefaultDeliveryOptionRoad(deliveryOptionRoad)
            updatedValue = {
                ...updatedValue,
                numberOfTrucks: undefined,
                numberOfVans: undefined,
            }
        }

        if (isRoadDelivery && (defaultTransportModeForwarder !== transportModeForwarder)) {
            setTransportModeForwarder(transportModeForwarder)
            updatedValue = {
                ...updatedValue,
                deliveryOptionRoad: undefined,
                numberOfTrucks: undefined,
                numberOfVans: undefined,
            }
        }

        if (transportModeSea === TRANSPORT_MODE_LCL) {
            updatedValue = {
                ...updatedValue,
                seaFreightContainerSize: undefined,
                numberOf40FeetContainers: undefined,
                numberOf20FeetContainers: undefined,
            }
        }

        if (!exists && defaultLocation?.id && defaultLocation?.id !== value[locationProperty]?.id) {
            updatedValue = {
                ...updatedValue,
                [locationProperty]: defaultLocation,
            }
        }

        if (!exists && stepTypeDetailed === StepTypeDetailed.EVENT_EVENT) {
            updatedValue = {
                ...updatedValue,
                eventLocation: defaultLocation,
            }
        }

        if (!exists && stepTypeDetailed === StepTypeDetailed.ROAD_FROM_EVENT) {
            updatedValue = {
                ...updatedValue,
                pickupLocationEvent: defaultLocation,
            }
        }

        if (exists && mainDelayReason && !isShowMainDelayReason) {
            updatedValue = {
                ...updatedValue,
                mainDelayReason: undefined,
                specificDelayReason: undefined,
                delayReasonComment: undefined,
            }
        }

        if (exists && mainDelayReason && specificDelayReason && !isShowSpecificDelayReason) {
            updatedValue = {
                ...updatedValue,
                specificDelayReason: undefined,
                delayReasonComment: undefined,
            }
        }

        if (exists && delayReasonComment && specificDelayReason !== SpecificDelayReason.OTHER) {
            updatedValue = {
                ...updatedValue,
                delayReasonComment: undefined,
            }
        }

        if (!isFirstStep && defaultPickupLocationContacts !== pickupLocationContacts) {
            setDefaultPickupLocationContacts(pickupLocationContacts)
            updatedValue = {
                ...updatedValue,
                pickupLocationContacts,
            }
        }

        // prepopulate forwarderContacts

        if (forwarder && forwarder.id !== defaultForwarderId && freightForwarderData) {
            setDefaultForwarderId(forwarder.id)
            updatedValue = {
                ...updatedValue,
                forwarderContacts: freightForwarderData?.contacts,
            }
        }

        if (isEmpty(updatedValue)) {
            return
        }

        const newValue = {
            ...value, ...updatedValue,
        }

        if (!isEqual(newValue, value)) {
            onChangeValue(newValue)
        }
    }, [
        isRoadDelivery,
        defaultLocation,
        defaultPickupLocationContacts,
        defaultTransportModeForwarder,
        defaultTransportModeRoad,
        exists,
        isFirstStep,
        locationProperty,
        onChangeValue,
        pickupLocationContacts,
        transportModeForwarder,
        transportModeRoad,
        transportModeSea,
        value,
        stepTypeDetailed,
        defaultDeliveryOptionRoad,
        deliveryOptionRoad,
        mainDelayReason,
        isShowMainDelayReason,
        specificDelayReason,
        isShowSpecificDelayReason,
        delayReasonComment,
        forwarder,
        forwarderContacts,
        freightForwarderData?.contacts,
        defaultForwarderId,
        freightForwarderData,
    ])

    return (
        <DomainObjectForm
            name={DOMAIN_NAME}
            className={classes.gridWrapper}
            value={value}
            fields={fields}
            onChange={onChangeValue}
            wrapper="simple"
            disabled={disabled}
        />
    )
}

OrderStepsForm.defaultProps = defaultProps

export default OrderStepsForm
